/**
 * Daily Sign-In Page
 * Refactored to use modular components for better maintainability
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CalendarIcon, RefreshCw, Printer, Search, X, Filter, RotateCcw } from "lucide-react";
import axios from "axios";
import { format, parse, addDays, subDays } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { playOwnerActivityAlert } from "@/utils/notificationSound";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import AppShell from "@/components/layout/AppShell";
import DailySignInHeroCarousel from "@/components/daily-signin/DailySignInHeroCarousel";

// Import shared constants for field names (prevents typos!)
import { 
  MANUAL_ENTRY_TYPES, 
  buildManualEntryData 
} from "@/constants/signinFields";

// Import modular components
import {
  SignInHeader,
  AttendanceSummary,
  RegularSignInCard,
  SchoolAgeSignInCard,
  CenterTeamMessage
} from "@/components/daily-signin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Owner/Co-Owner emails - these users will receive beeps from staff and vice versa
const OWNER_EMAILS = ["todayskidz@comcast.net", "todayskidz45@gmail.com"];

const DailySignInPage = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const { user } = useAuth();
  
  // Feature access checks
  const { hasAccess: hasCenterAlertsAccess, currentTier } = useFeatureAccess('center_alerts');
  const isViewOnly = currentTier === 'expired';
  
  // Guard: show toast and block write action when trial expired
  const blockIfExpired = () => {
    if (isViewOnly) {
      toast.error("Trial ended — upgrade to sign children in/out. Use the Upgrade Now banner above.");
      return true;
    }
    return false;
  };
  
  // State
  const [children, setChildren] = useState([]);
  const [signIns, setSignIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingMonth, setGeneratingMonth] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false); // Flag to prevent background sync during time edit
  const [amBusPickUpTime, setAmBusPickUpTime] = useState(null); // AM bus pickup timestamp — gates School Age donut reset
  
  // Filter/Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all"); // "all", "full-time", "part-time", "school-age", "after-school"
  
  // Track last known owner update timestamp (for beep notification)
  const lastKnownOwnerUpdateRef = useRef(null);
  // Track last known staff update timestamp (for owner beep notification)
  const lastKnownStaffUpdateRef = useRef(null);
  // Track last known other owner update timestamp (for peer-to-peer owner beep notification)
  const lastKnownOtherOwnerUpdateRef = useRef(null);
  // Track last known other staff update timestamp (for peer-to-peer staff beep notification)
  const lastKnownOtherStaffUpdateRef = useRef(null);
  // Debounce timeout for notes saving - only saves ONCE after typing stops
  const notesDebounceRef = useRef({});
  
  // Check if current user is staff (not an owner or co-owner) - they receive beep when owners/co-owners update
  const isStaffUser = user && !OWNER_EMAILS.includes(user.email?.toLowerCase()) && user.role !== 'owner' && user.role?.toLowerCase() !== 'co-owner';
  // Check if current user is owner or co-owner - they receive beep when staff updates notes
  const isOwnerUser = user && (OWNER_EMAILS.includes(user.email?.toLowerCase()) || user.role === 'owner' || user.role?.toLowerCase() === 'co-owner');

  // ============== DATA FETCHING ==============
  
  const fetchLatestSignIns = async () => {
    try {
      const signInsRes = await axios.get(`${API}/signins?date=${date}`);
      // Directly replace all sign-ins with server data to ensure real-time sync
      // This ensures deletions, updates, and additions are all reflected
      // Exception: preserve locally typed notes for any child mid-debounce
      if (signInsRes.data) {
        setSignIns(prev =>
          signInsRes.data.map(serverSignIn => {
            if (notesDebounceRef.current[serverSignIn.id]) {
              const local = prev.find(s => s.id === serverSignIn.id);
              return local ? { ...serverSignIn, notes: local.notes } : serverSignIn;
            }
            return serverSignIn;
          })
        );
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.log("Background refresh failed:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch ALL children (including inactive) so we can show them on historical dates
      const childrenRes = await axios.get(`${API}/children`);
      const sortedChildren = childrenRes.data.sort((a, b) => a.name.localeCompare(b.name));
      setChildren(sortedChildren);
      
      // Get only active children for creating new sign-ins
      const activeChildren = sortedChildren.filter(c => c.active !== false);

      // Fetch sign-ins for this date
      const signInsRes = await axios.get(`${API}/signins?date=${date}`);
      
      // If no sign-ins exist for this date, create them for ACTIVE children only
      if (signInsRes.data.length === 0 && activeChildren.length > 0) {
        const newSignIns = [];
        for (const child of activeChildren) {
          const newSignIn = {
            date: date,
            childId: child.id,
            childName: child.name,
            category: child.category,
            attendance: "absent"
          };
          const response = await axios.post(`${API}/signins`, newSignIn);
          newSignIns.push(response.data);
        }
        setSignIns(newSignIns);
      } else {
        // Check if any ACTIVE children are missing from existing sign-ins
        const existingChildIds = signInsRes.data.map(s => s.childId);
        const missingChildren = activeChildren.filter(c => !existingChildIds.includes(c.id));
        
        if (missingChildren.length > 0) {
          const newSignIns = [];
          for (const child of missingChildren) {
            const newSignIn = {
              date: date,
              childId: child.id,
              childName: child.name,
              category: child.category,
              attendance: "absent"
            };
            const response = await axios.post(`${API}/signins`, newSignIn);
            newSignIns.push(response.data);
          }
          setSignIns([...signInsRes.data, ...newSignIns]);
        } else {
          setSignIns(signInsRes.data);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load sign-in data");
    }
    setLoading(false);
  };

  // ============== EFFECTS ==============

  // Fetch AM bus pickup time for the day — gates School Age donut "reset" behavior
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    const fetchBus = async () => {
      try {
        const res = await axios.get(`${API}/bus-tracking/${date}`);
        if (cancelled) return;
        const tracking = res.data.tracking || {};
        // am_pickup is now a list — one entry per school; any entry recorded today gates the donut reset
        setAmBusPickUpTime(tracking.am_pickup?.[0]?.arrivalTime || null);
      } catch {
        if (!cancelled) setAmBusPickUpTime(null);
      }
    };
    fetchBus();
    // Light poll so the donut flips when AttendanceSummary records the bus pickup
    const t = setInterval(fetchBus, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [date]);

  // Poll for owner updates every 3 seconds - plays BEEP and syncs data IMMEDIATELY when detected
  // This is for STAFF users to hear beep when OWNERS update notes
  useEffect(() => {
    if (!isStaffUser || !date) return;
    
    const checkOwnerUpdates = async () => {
      // Skip if user is editing time
      if (isEditingTime) return;
      
      try {
        const response = await axios.get(`${API}/signins/last-owner-update?date=${date}`);
        const { hasOwnerUpdate, lastModifiedAt } = response.data;
        
        if (hasOwnerUpdate && lastModifiedAt) {
          if (lastKnownOwnerUpdateRef.current && lastKnownOwnerUpdateRef.current !== lastModifiedAt) {
            playOwnerActivityAlert();
            fetchLatestSignIns();
          }
          lastKnownOwnerUpdateRef.current = lastModifiedAt;
        }
      } catch (error) {
        // Silent fail for background check
      }
    };
    
    checkOwnerUpdates();
    const pollInterval = setInterval(checkOwnerUpdates, 3000);
    return () => clearInterval(pollInterval);
  }, [date, isStaffUser, isEditingTime]);

  // Poll for OTHER STAFF note updates every 3 seconds - plays BEEP for STAFF when OTHER STAFF adds notes
  // This enables peer-to-peer notifications between staff members in different classes
  useEffect(() => {
    if (!isStaffUser || !date) return;
    
    const checkOtherStaffUpdates = async () => {
      // Skip if user is editing time
      if (isEditingTime) return;
      
      try {
        const response = await axios.get(`${API}/signins/last-other-staff-update?date=${date}`);
        const { hasOtherStaffUpdate, staffNotesUpdatedAt, staffNotesUpdatedBy, childName } = response.data;
        
        if (hasOtherStaffUpdate && staffNotesUpdatedAt) {
          if (lastKnownOtherStaffUpdateRef.current && lastKnownOtherStaffUpdateRef.current !== staffNotesUpdatedAt) {
            playOwnerActivityAlert();
            fetchLatestSignIns();
            // Show who added the note
            toast.info(`${staffNotesUpdatedBy} added a note for ${childName}`, { duration: 4000 });
          }
          lastKnownOtherStaffUpdateRef.current = staffNotesUpdatedAt;
        }
      } catch (error) {
        // Silent fail for background check
      }
    };
    
    checkOtherStaffUpdates();
    const pollInterval = setInterval(checkOtherStaffUpdates, 3000);
    return () => clearInterval(pollInterval);
  }, [date, isStaffUser, isEditingTime]);

  // Poll for staff note updates every 3 seconds - plays BEEP for OWNERS when STAFF adds notes
  useEffect(() => {
    if (!isOwnerUser || !date) return;
    
    const checkStaffUpdates = async () => {
      // Skip if user is editing time
      if (isEditingTime) return;
      
      try {
        const response = await axios.get(`${API}/signins/last-staff-update?date=${date}`);
        const { hasStaffUpdate, staffNotesUpdatedAt, staffNotesUpdatedBy, childName } = response.data;
        
        if (hasStaffUpdate && staffNotesUpdatedAt) {
          if (lastKnownStaffUpdateRef.current && lastKnownStaffUpdateRef.current !== staffNotesUpdatedAt) {
            playOwnerActivityAlert();
            fetchLatestSignIns();
            // Optional: Show who added the note
            toast.info(`${staffNotesUpdatedBy} added a note for ${childName}`, { duration: 4000 });
          }
          lastKnownStaffUpdateRef.current = staffNotesUpdatedAt;
        }
      } catch (error) {
        // Silent fail for background check
      }
    };
    
    checkStaffUpdates();
    const pollInterval = setInterval(checkStaffUpdates, 3000);
    return () => clearInterval(pollInterval);
  }, [date, isOwnerUser, isEditingTime]);

  // Poll for OTHER OWNER note updates every 3 seconds - plays BEEP for OWNERS when OTHER OWNERS add notes
  // This enables peer-to-peer notifications between primary owner and co-owners
  useEffect(() => {
    if (!isOwnerUser || !date) return;
    
    const checkOtherOwnerUpdates = async () => {
      // Skip if user is editing time
      if (isEditingTime) return;
      
      try {
        const response = await axios.get(`${API}/signins/last-other-owner-update?date=${date}`);
        const { hasOtherOwnerUpdate, lastModifiedAt, lastModifiedBy, childName } = response.data;
        
        if (hasOtherOwnerUpdate && lastModifiedAt) {
          if (lastKnownOtherOwnerUpdateRef.current && lastKnownOtherOwnerUpdateRef.current !== lastModifiedAt) {
            playOwnerActivityAlert();
            fetchLatestSignIns();
            // Show who added the note
            toast.info(`${lastModifiedBy} added a note for ${childName}`, { duration: 4000 });
          }
          lastKnownOtherOwnerUpdateRef.current = lastModifiedAt;
        }
      } catch (error) {
        // Silent fail for background check
      }
    };
    
    checkOtherOwnerUpdates();
    const pollInterval = setInterval(checkOtherOwnerUpdates, 3000);
    return () => clearInterval(pollInterval);
  }, [date, isOwnerUser, isEditingTime]);

  // Background sync every 3 seconds for ALL users to get real-time updates
  // This ensures pick-ups, drop-offs, and other changes are reflected in real-time
  // SKIP sync when user is editing time to prevent overwriting their input
  useEffect(() => {
    if (!date) return;
    
    const refreshInterval = setInterval(async () => {
      if (!loading && !isEditingTime) {
        await fetchLatestSignIns();
      }
    }, 3000);

    return () => clearInterval(refreshInterval);
  }, [date, loading, isEditingTime]);

  useEffect(() => {
    fetchData();
  }, [date]);

  // ============== HANDLERS ==============
  
  const handleDropOff = async (signIn, personName) => {
    if (blockIfExpired()) return;
    try {
      const currentTime = format(new Date(), 'h:mm a');
      const updateData = {
        dropOffPerson: personName,
        dropOffTime: currentTime,
        attendance: "present"
      };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, ...updateData} : s
      ));
      toast.success(`${signIn.childName} dropped off`);
    } catch (error) {
      console.error("Error updating drop-off:", error);
      toast.error("Failed to update drop-off");
    }
  };

  const handleClearDropOff = async (signIn) => {
    if (blockIfExpired()) return;
    try {
      const updateData = {
        clearFields: ["dropOffPerson", "dropOffTime"],
        attendance: signIn.pickUpTime ? "present" : "absent"
      };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id 
          ? {...s, dropOffPerson: null, dropOffTime: null, attendance: updateData.attendance} 
          : s
      ));
      toast.success(`Drop-off cleared for ${signIn.childName}`);
    } catch (error) {
      console.error("Error clearing drop-off:", error);
      toast.error("Failed to clear drop-off");
    }
  };

  const handlePickUp = async (signIn, personName) => {
    if (blockIfExpired()) return;
    try {
      const currentTime = format(new Date(), 'h:mm a');
      const updateData = {
        pickUpPerson: personName,
        pickUpTime: currentTime
      };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, ...updateData} : s
      ));
      toast.success(`${signIn.childName} picked up`);
    } catch (error) {
      console.error("Error updating pick-up:", error);
      toast.error("Failed to update pick-up");
    }
  };

  const handleClearPickUp = async (signIn) => {
    if (blockIfExpired()) return;
    try {
      const updateData = { clearFields: ["pickUpPerson", "pickUpTime"] };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, pickUpPerson: null, pickUpTime: null} : s
      ));
      toast.success(`Pick-up cleared for ${signIn.childName}`);
    } catch (error) {
      console.error("Error clearing pick-up:", error);
      toast.error("Failed to clear pick-up");
    }
  };

  const handleManualTimeUpdate = async (signIn, field, value) => {
    if (blockIfExpired()) return;
    // Set flag to prevent background sync from overwriting the edit
    setIsEditingTime(true);
    setSignIns(prev => prev.map(s => 
      s.id === signIn.id ? {...s, [field]: value} : s
    ));
  };

  const saveTimeToServer = async (signIn, field, value) => {
    try {
      await axios.put(`${API}/signins/${signIn.id}`, { [field]: value });
      // Update local state to ensure consistency
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, [field]: value} : s
      ));
      toast.success("Time saved");
    } catch (error) {
      console.error("Error saving time:", error);
      toast.error("Failed to save time");
      // Refetch to restore correct state on error
      fetchLatestSignIns();
    } finally {
      // Re-enable background sync after save completes
      setIsEditingTime(false);
    }
  };

  // School Age specific handlers
  const getPickupPersonsForChild = (childId) => {
    const child = children.find(c => c.id === childId);
    return child?.pickupPersons || [];
  };

  // Handler for manual person entry (not on pickup list)
  // Uses shared constants from signinFields.js to prevent field name typos
  const handleManualPersonEntry = async (signIn, type, personNameWithApproval) => {
    if (blockIfExpired()) return;
    try {
      const currentTime = format(new Date(), 'h:mm a');
      
      // Build update data using shared constants (prevents typos!)
      const updateData = buildManualEntryData(type, personNameWithApproval, currentTime);
      
      if (Object.keys(updateData).length === 0) {
        toast.error("Unknown entry type");
        return;
      }
      
      // Show appropriate success message
      const personName = personNameWithApproval.split(' [')[0];
      const messages = {
        [MANUAL_ENTRY_TYPES.DROP_OFF]: `${signIn.childName} dropped off by ${personName}`,
        [MANUAL_ENTRY_TYPES.PICK_UP]: `${signIn.childName} picked up by ${personName}`,
        [MANUAL_ENTRY_TYPES.AM_DROP_OFF]: `${signIn.childName} AM dropped off by ${personName}`,
        [MANUAL_ENTRY_TYPES.PM_PICK_UP]: `${signIn.childName} PM picked up by ${personName}`,
      };
      
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, ...updateData} : s
      ));
      
      toast.success(messages[type] || "Recorded successfully");
    } catch (error) {
      console.error("Error recording manual person:", error);
      toast.error("Failed to record");
    }
  };

  const handleAmDropOff = async (signIn, dropOffPerson) => {
    if (blockIfExpired()) return;
    try {
      const currentTime = format(new Date(), 'h:mm a');
      const updateData = { 
        amParentDropOffTime: currentTime, 
        amDropOffPerson: dropOffPerson,
        attendance: "present" 
      };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, ...updateData} : s
      ));
      toast.success(`${signIn.childName} AM drop off by ${dropOffPerson} recorded`);
    } catch (error) {
      console.error("Error updating AM drop off:", error);
      toast.error("Failed to record AM drop off");
    }
  };

  const handleClearAmDropOff = async (signIn) => {
    if (blockIfExpired()) return;
    try {
      const updateData = { clearFields: ["amParentDropOffTime", "amDropOffPerson"] };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, amParentDropOffTime: null, amDropOffPerson: null} : s
      ));
      toast.success(`AM drop off cleared for ${signIn.childName}`);
    } catch (error) {
      console.error("Error clearing AM drop off:", error);
      toast.error("Failed to clear AM drop off");
    }
  };

  const handlePmBusDropoff = async (signIn) => {
    if (blockIfExpired()) return;
    try {
      const currentTime = format(new Date(), 'h:mm a');
      const updateData = { pmSchoolAgeDropOffTime: currentTime, pmSchoolAgeDropOffPerson: user?.name || user?.email || '', attendance: "present" };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, ...updateData} : s
      ));
      toast.success(`${signIn.childName} PM bus dropoff recorded`);
    } catch (error) {
      console.error("Error updating PM bus dropoff:", error);
      toast.error("Failed to record PM bus dropoff");
    }
  };

  const handleClearPmBusDropoff = async (signIn) => {
    if (blockIfExpired()) return;
    try {
      const updateData = { clearFields: ["pmSchoolAgeDropOffTime", "pmSchoolAgeDropOffPerson"] };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, pmSchoolAgeDropOffTime: null, pmSchoolAgeDropOffPerson: null} : s
      ));
      toast.success(`PM bus dropoff cleared for ${signIn.childName}`);
    } catch (error) {
      console.error("Error clearing PM bus dropoff:", error);
      toast.error("Failed to clear PM bus dropoff");
    }
  };

  const handlePmParentPickup = async (signIn, personName) => {
    if (blockIfExpired()) return;
    try {
      const currentTime = format(new Date(), 'h:mm a');
      const updateData = { 
        pmParentPickupTime: currentTime, 
        pmParentPickupPerson: personName 
      };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, ...updateData} : s
      ));
      toast.success(`${signIn.childName} picked up by ${personName}`);
    } catch (error) {
      console.error("Error updating PM parent pickup:", error);
      toast.error("Failed to record pickup");
    }
  };

  const handleClearPmParentPickup = async (signIn) => {
    if (blockIfExpired()) return;
    try {
      const updateData = { clearFields: ["pmParentPickupTime", "pmParentPickupPerson"] };
      await axios.put(`${API}/signins/${signIn.id}`, updateData);
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, pmParentPickupTime: null, pmParentPickupPerson: null} : s
      ));
      toast.success(`PM pickup cleared for ${signIn.childName}`);
    } catch (error) {
      console.error("Error clearing PM pickup:", error);
      toast.error("Failed to clear PM pickup");
    }
  };

  // Debounced notes update - only saves ONCE after 2 seconds of no typing
  // This prevents beeping on every keystroke - only beeps when typing is truly finished
  const handleNotesUpdate = (signIn, notes) => {
    if (blockIfExpired()) return;
    // Update local state immediately for responsive UI
    setSignIns(prev => prev.map(s => 
      s.id === signIn.id ? {...s, notes: notes} : s
    ));
    
    // Clear existing timeout for this sign-in
    if (notesDebounceRef.current[signIn.id]) {
      clearTimeout(notesDebounceRef.current[signIn.id]);
    }
    
    // Save after 2 seconds of no typing (when typing is truly finished)
    notesDebounceRef.current[signIn.id] = setTimeout(async () => {
      try {
        await axios.put(`${API}/signins/${signIn.id}`, { notes });
      } catch (error) {
        console.error("Error updating notes:", error);
        toast.error("Failed to save notes");
      }
    }, 2000);
  };

  const handleAttendanceToggle = async (signIn, checked) => {
    if (blockIfExpired()) return;
    try {
      const newAttendance = checked ? "present" : "absent";
      await axios.put(`${API}/signins/${signIn.id}`, { attendance: newAttendance });
      setSignIns(prev => prev.map(s => 
        s.id === signIn.id ? {...s, attendance: newAttendance} : s
      ));
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error("Failed to update attendance");
    }
  };

  const handleGenerateMonth = async () => {
    if (blockIfExpired()) return;
    if (!date) return;
    
    const confirmMsg = "This will generate sign-in records for all days in this month. Continue?";
    if (!window.confirm(confirmMsg)) return;

    setGeneratingMonth(true);
    try {
      const month = date.substring(0, 7);
      const response = await axios.post(`${API}/signins/bulk-create`, { month });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Error generating month:", error);
      toast.error("Failed to generate month records");
    }
    setGeneratingMonth(false);
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const signInsRes = await axios.get(`${API}/signins?date=${date}`);
      if (signInsRes.data.length > 0) {
        setSignIns(signInsRes.data);
        setLastRefresh(new Date());
        toast.success("Saved", { duration: 2000 });
      }
    } catch (error) {
      console.error("Refresh failed:", error);
    }
    setRefreshing(false);
  };

  const handleReactivate = async (child) => {
    if (blockIfExpired()) return;
    try {
      await axios.post(`${API}/children/${child.id}/reactivate`);
      toast.success(`${child.name} reactivated`);
      fetchData();
    } catch (error) {
      toast.error("Failed to reactivate");
    }
  };

  // ---- Stay All Day (School Age → Full Time ratio) ----
  const handleStayAllDayToggle = async (signIn, value) => {
    if (blockIfExpired()) return;
    try {
      await axios.put(`${API}/signins/${signIn.id}`, { stayAllDay: value });
      setSignIns(prev => prev.map(s =>
        s.id === signIn.id ? { ...s, stayAllDay: value } : s
      ));
      toast.success(value ? `${signIn.childName} marked Staying All Day` : `Stay All Day removed for ${signIn.childName}`);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // ---- Mid-Day Departure (all categories) ----
  const handleMidDayOut = async (signIn, reason, customReason) => {
    if (blockIfExpired()) return;
    try {
      const outTime = format(new Date(), 'h:mm a');
      const midDayOut = { outTime, reason, customReason: customReason || '', returnTime: null };
      await axios.put(`${API}/signins/${signIn.id}`, { midDayOut });
      setSignIns(prev => prev.map(s =>
        s.id === signIn.id ? { ...s, midDayOut } : s
      ));
      toast.success(`${signIn.childName} mid-day departure recorded`);
    } catch (error) {
      toast.error("Failed to record mid-day departure");
    }
  };

  const handleMidDayReturn = async (signIn) => {
    try {
      const returnTime = format(new Date(), 'h:mm a');
      const midDayOut = { ...signIn.midDayOut, returnTime };
      await axios.put(`${API}/signins/${signIn.id}`, { midDayOut });
      setSignIns(prev => prev.map(s =>
        s.id === signIn.id ? { ...s, midDayOut } : s
      ));
      toast.success(`${signIn.childName} returned`);
    } catch (error) {
      toast.error("Failed to record return");
    }
  };

  const handleClearMidDay = async (signIn) => {
    try {
      await axios.put(`${API}/signins/${signIn.id}`, { clearFields: ['midDayOut'] });
      setSignIns(prev => prev.map(s =>
        s.id === signIn.id ? { ...s, midDayOut: null } : s
      ));
      toast.success(`Mid-day departure cleared for ${signIn.childName}`);
    } catch (error) {
      toast.error("Failed to clear mid-day departure");
    }
  };

  // ============== HELPERS ==============
  
  const getChild = (childId) => children.find(c => c.id === childId);

  // Hide deactivated children from the list unless they already have real attendance
  // activity today (so an in-progress pickup can still be finished after deactivation).
  // Fixes empty leftover placeholder rows from "Generate Full Month".
  const hasAttendanceActivity = (s) =>
    !!(s.dropOffTime || s.pickUpTime || s.amParentDropOffTime || s.pmSchoolAgeDropOffTime ||
       s.pmParentPickupTime || s.midDayOut?.outTime);
  const visibleSignIns = signIns.filter(s => {
    const child = getChild(s.childId);
    return !child || child.active !== false || hasAttendanceActivity(s);
  });

  // Apply search filter to signIns
  const searchFilteredSignIns = searchQuery.trim() 
    ? visibleSignIns.filter(s => s.childName?.toLowerCase().includes(searchQuery.toLowerCase()))
    : visibleSignIns;

  // Filter sign-ins by category - handle both old format ('Full Time') and new format ('full_time')
  const fullTimeSignIns = searchFilteredSignIns.filter(s => 
    s.category === 'full_time' || s.category === 'regular' || s.category === 'Full Time'
  ).sort((a, b) => a.childName.localeCompare(b.childName));
  const partTimeSignIns = searchFilteredSignIns.filter(s => 
    s.category === 'part_time' || s.category === 'Part Time'
  ).sort((a, b) => a.childName.localeCompare(b.childName));
  const schoolAgeSignIns = searchFilteredSignIns.filter(s => 
    s.category === 'school_age' || s.category === 'School Age'
  ).sort((a, b) => a.childName.localeCompare(b.childName));
  const afterSchoolSignIns = searchFilteredSignIns.filter(s => 
    s.category === 'after_school' || s.category === 'After School'
  ).sort((a, b) => a.childName.localeCompare(b.childName));
  const uncategorizedSignIns = searchFilteredSignIns.filter(s => 
    !s.category || !['full_time', 'part_time', 'school_age', 'after_school', 'regular', 'Full Time', 'Part Time', 'School Age', 'After School'].includes(s.category)
  ).sort((a, b) => a.childName.localeCompare(b.childName));

  // Determine which categories to show based on filter
  const showFullTime = categoryFilter === 'all' || categoryFilter === 'full-time';
  const showPartTime = categoryFilter === 'all' || categoryFilter === 'part-time';
  const showSchoolAge = categoryFilter === 'all' || categoryFilter === 'school-age';
  const showAfterSchool = categoryFilter === 'all' || categoryFilter === 'after-school';
  const showUncategorized = categoryFilter === 'all';

  // Check if any filters are active
  const hasActiveFilters = searchQuery.trim() !== "" || categoryFilter !== "all";

  // Reset filters handler
  const resetFilters = () => {
    setSearchQuery("");
    setCategoryFilter("all");
  };

  // Create lookup map for children by ID to get subcategory
  const childrenById = {};
  children.forEach(c => { childrenById[c.id] = c; });
  
  // Get Full Time subcategory for a signin - handle both old and new formats
  const getFullTimeSubcategory = (signin) => {
    const child = childrenById[signin.childId];
    const subcat = child?.fullTimeSubcategory;
    // Normalize to lowercase format
    if (subcat === 'PreK' || subcat === 'prek') return 'prek';
    if (subcat === 'Older Toddler' || subcat === 'older_toddler') return 'older_toddler';
    if (subcat === 'Young Toddler' || subcat === 'young_toddler') return 'young_toddler';
    if (subcat === 'Infant' || subcat === 'infant') return 'infant';
    return null;
  };
  
  // Full Time subcategory counts — TWO lenses for clarity:
  // (a) "currentlyIn" = dropOff but NOT yet picked up (matches headline "9 currently")
  // (b) "cameToday"   = anyone who had a dropOff today, including kids already picked up
  const signedInFullTime = fullTimeSignIns.filter(s => s.dropOffTime);                       // came today
  const currentlyInFullTime = fullTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime);   // still here
  const signedOutFullTime = fullTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime);

  const fullTimeSubcategoryCounts = {
    // Counts of children who CAME TODAY (matches subcategory "came today" lens)
    prek: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'prek').length,
    olderToddler: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'older_toddler').length,
    youngToddler: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'young_toddler').length,
    infant: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'infant').length,
    // Stay-All-Day School Age children — counted here per state ratio rule
    stayAllDay: 0, // computed below after stayAllDay variables are defined
    // Counts of children CURRENTLY IN BUILDING (matches headline "currently" lens)
    prekIn: currentlyInFullTime.filter(s => getFullTimeSubcategory(s) === 'prek').length,
    olderToddlerIn: currentlyInFullTime.filter(s => getFullTimeSubcategory(s) === 'older_toddler').length,
    youngToddlerIn: currentlyInFullTime.filter(s => getFullTimeSubcategory(s) === 'young_toddler').length,
    infantIn: currentlyInFullTime.filter(s => getFullTimeSubcategory(s) === 'infant').length,
    stayAllDayIn: 0, // computed below
  };

  // Full Time subcategory children lists (for hover tooltips) - only those with timestamps
  const fullTimeSubcategoryChildren = {
    prek: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'prek').map(s => childrenById[s.childId]?.name || 'Unknown'),
    olderToddler: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'older_toddler').map(s => childrenById[s.childId]?.name || 'Unknown'),
    youngToddler: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'young_toddler').map(s => childrenById[s.childId]?.name || 'Unknown'),
    infant: signedInFullTime.filter(s => getFullTimeSubcategory(s) === 'infant').map(s => childrenById[s.childId]?.name || 'Unknown'),
    stayAllDay: [], // computed below
    signedOut: signedOutFullTime.map(s => childrenById[s.childId]?.name || 'Unknown'),
  };

  // Signed Out subcategory counts (for the new Signed Out by Category card)
  const signedOutSubcategoryCounts = {
    prek:         signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'prek').length,
    olderToddler: signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'older_toddler').length,
    youngToddler: signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'young_toddler').length,
    infant:       signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'infant').length,
    stayAllDay:   0, // computed below
  };

  // Signed Out subcategory name lists (for hover tooltips on the new card)
  const signedOutSubcategoryChildren = {
    prek:         signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'prek').map(s => childrenById[s.childId]?.name || 'Unknown'),
    olderToddler: signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'older_toddler').map(s => childrenById[s.childId]?.name || 'Unknown'),
    youngToddler: signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'young_toddler').map(s => childrenById[s.childId]?.name || 'Unknown'),
    infant:       signedOutFullTime.filter(s => getFullTimeSubcategory(s) === 'infant').map(s => childrenById[s.childId]?.name || 'Unknown'),
    stayAllDay:   [], // computed below
  };

  // School Age children lists (for hover tooltips)
  const schoolAgeChildrenLists = {
    amSignedIn: schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.stayAllDay).map(s => childrenById[s.childId]?.name || 'Unknown'),
    pmDropOff: schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime).map(s => childrenById[s.childId]?.name || 'Unknown'),
    pmOut: schoolAgeSignIns.filter(s => s.pmParentPickupTime && !s.stayAllDay).map(s => childrenById[s.childId]?.name || 'Unknown'),
  };

  // Stay All Day — School Age children counted in Full Time ratio
  const stayAllDaySignIns = schoolAgeSignIns.filter(s => s.stayAllDay === true);
  const stayAllDayCurrentlyIn = stayAllDaySignIns.filter(s => s.amParentDropOffTime && !s.pmParentPickupTime);
  const stayAllDaySignedOut = stayAllDaySignIns.filter(s => s.pmParentPickupTime);

  // Backfill Stay-All-Day into the Full Time subcategory rows (per ratio rule)
  fullTimeSubcategoryCounts.stayAllDay = stayAllDaySignIns.length;        // came today (anyone who started)
  fullTimeSubcategoryCounts.stayAllDayIn = stayAllDayCurrentlyIn.length;  // currently in
  fullTimeSubcategoryChildren.stayAllDay = stayAllDaySignIns.map(s => childrenById[s.childId]?.name || 'Unknown');
  signedOutSubcategoryCounts.stayAllDay = stayAllDaySignedOut.length;
  signedOutSubcategoryChildren.stayAllDay = stayAllDaySignedOut.map(s => childrenById[s.childId]?.name || 'Unknown');

  // Mid-Day Departure summary (all categories)
  const allCategorySignIns = [...fullTimeSignIns, ...partTimeSignIns, ...schoolAgeSignIns, ...afterSchoolSignIns];
  const midDayCurrentlyOut = allCategorySignIns.filter(s => s.midDayOut?.outTime && !s.midDayOut?.returnTime);
  const midDayReturnedToday = allCategorySignIns.filter(s => s.midDayOut?.outTime && s.midDayOut?.returnTime);

  // Part Time & After School children lists (for hover tooltips)
  const partTimeChildrenList = partTimeSignIns.filter(s => s.dropOffTime).map(s => childrenById[s.childId]?.name || 'Unknown');
  const afterSchoolChildrenList = afterSchoolSignIns.filter(s => s.dropOffTime).map(s => childrenById[s.childId]?.name || 'Unknown');

  // Calculate counts - restored to original working logic
  const getCategoryCounts = (categorySignIns) => {
    const signedIn = categorySignIns.filter(s => s.dropOffTime && !s.pickUpTime).length;
    const signedOut = categorySignIns.filter(s => s.dropOffTime && s.pickUpTime).length;
    return { signedIn, signedOut, total: categorySignIns.length };
  };

  const getSchoolAgeCounts = (categorySignIns) => {
    const amSignedIn = categorySignIns.filter(s => s.amParentDropOffTime && !s.stayAllDay && !s.amBusDepartureTime).length;
    const pmDropOff = categorySignIns.filter(s => s.pmSchoolAgeDropOffTime).length;
    const pmPickedUp = categorySignIns.filter(s => s.pmParentPickupTime && !s.stayAllDay).length;
    const isAfternoonMode = pmDropOff > 0;
    
    let currentlySignedIn;
    if (isAfternoonMode) {
      currentlySignedIn = categorySignIns.filter(s => 
        s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime
      ).length;
    } else {
      currentlySignedIn = amSignedIn;
    }
    
    return { amSignedIn, pmDropOff, pmPickedUp, currentlySignedIn, isAfternoonMode, total: categorySignIns.length };
  };

  const rawFullTimeCounts = getCategoryCounts(fullTimeSignIns);
  const fullTimeCounts = {
    ...rawFullTimeCounts,
    signedIn: rawFullTimeCounts.signedIn + stayAllDayCurrentlyIn.length,
    signedOut: rawFullTimeCounts.signedOut + stayAllDaySignedOut.length,
    cameToday: signedInFullTime.length + stayAllDaySignIns.length,
  };
  const partTimeCounts = getCategoryCounts(partTimeSignIns);
  const schoolAgeCounts = getSchoolAgeCounts(schoolAgeSignIns);
  const afterSchoolCounts = getCategoryCounts(afterSchoolSignIns);
  const uncategorizedCounts = getCategoryCounts(uncategorizedSignIns);

  const totalSignedIn = fullTimeCounts.signedIn + partTimeCounts.signedIn + schoolAgeCounts.currentlySignedIn + afterSchoolCounts.signedIn + uncategorizedCounts.signedIn;
  const totalSignedOut = fullTimeCounts.signedOut + partTimeCounts.signedOut + schoolAgeCounts.pmPickedUp + afterSchoolCounts.signedOut + uncategorizedCounts.signedOut;
  const totalChildren = signIns.length;

  // ===== "Still In Center By Category" (ratio compliance view) =====
  // School Age — split currently-in into AM-only vs PM-only (excludes Stay-All-Day; those count in Full Time)
  const schoolAgeAMOnlyIn = schoolAgeSignIns.filter(s =>
    s.amParentDropOffTime && !s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime && !s.stayAllDay && !s.amBusDepartureTime
  ).length;
  const schoolAgePMOnlyIn = schoolAgeSignIns.filter(s =>
    s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime
  ).length;
  const schoolAgeAMCame = schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.stayAllDay).length;
  const schoolAgePMCame = schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime).length;

  // Helper: current-in subcategory names (Full Time)
  const currentlyInFullTimeNames = (subcat) => currentlyInFullTime
    .filter(s => getFullTimeSubcategory(s) === subcat)
    .map(s => childrenById[s.childId]?.name || 'Unknown');

  const stillInData = {
    fullTime: {
      stillIn: fullTimeCounts.signedIn,
      total: fullTimeCounts.cameToday ?? (signedInFullTime.length + stayAllDaySignIns.length),
      rows: [
        { label: 'PreK', value: fullTimeSubcategoryCounts.prekIn || 0, names: currentlyInFullTimeNames('prek') },
        { label: 'Young Toddler', value: fullTimeSubcategoryCounts.youngToddlerIn || 0, names: currentlyInFullTimeNames('young_toddler') },
        { label: 'Older Toddler', value: fullTimeSubcategoryCounts.olderToddlerIn || 0, names: currentlyInFullTimeNames('older_toddler') },
        { label: 'Infant', value: fullTimeSubcategoryCounts.infantIn || 0, names: currentlyInFullTimeNames('infant') },
        ...(stayAllDayCurrentlyIn.length > 0
          ? [{ label: 'School Age Staying All Day', value: stayAllDayCurrentlyIn.length, names: stayAllDayCurrentlyIn.map(s => childrenById[s.childId]?.name || s.childName || 'Unknown') }]
          : []),
      ],
    },
    schoolAge: amBusPickUpTime ? {
      // AM bus has picked up — donut resets and tracks PM drop-offs going forward
      stillIn: schoolAgePMOnlyIn,
      total: schoolAgePMCame,
      rows: [
        { label: 'Still Here', value: schoolAgePMOnlyIn, names: schoolAgeSignIns.filter(s => s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime).map(s => childrenById[s.childId]?.name || 'Unknown') },
      ],
    } : {
      // Before AM bus pickup — show AM kids physically at the center waiting for bus
      stillIn: schoolAgeAMOnlyIn,
      total: schoolAgeAMCame,
      rows: [
        { label: 'Still Here', value: schoolAgeAMOnlyIn, names: schoolAgeSignIns.filter(s => s.amParentDropOffTime && !s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime && !s.stayAllDay && !s.amBusDepartureTime).map(s => childrenById[s.childId]?.name || 'Unknown') },
      ],
    },
    partTime: {
      stillIn: partTimeCounts.signedIn,
      total: partTimeCounts.signedIn + partTimeCounts.signedOut,
      rows: [
        { label: 'Still Here', value: partTimeCounts.signedIn, names: partTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime).map(s => childrenById[s.childId]?.name || 'Unknown') },
      ],
    },
    afterSchool: {
      stillIn: afterSchoolCounts.signedIn,
      total: afterSchoolCounts.signedIn + afterSchoolCounts.signedOut,
      rows: [
        { label: 'Still Here', value: afterSchoolCounts.signedIn, names: afterSchoolSignIns.filter(s => s.dropOffTime && !s.pickUpTime).map(s => childrenById[s.childId]?.name || 'Unknown') },
      ],
    },
  };

  // Total signed in/out children lists (for hover tooltips on bottom summary)
  const allSignedInChildren = [
    ...signedInFullTime.map(s => childrenById[s.childId]?.name || 'Unknown'),
    ...stayAllDayCurrentlyIn.map(s => s.childName || 'Unknown'),
    ...partTimeSignIns.filter(s => s.dropOffTime && !s.pickUpTime).map(s => childrenById[s.childId]?.name || 'Unknown'),
    ...schoolAgeSignIns.filter(s => (s.pmSchoolAgeDropOffTime && !s.pmParentPickupTime) || (s.amParentDropOffTime && !s.pmSchoolAgeDropOffTime && !s.stayAllDay && !s.amBusDepartureTime)).map(s => childrenById[s.childId]?.name || 'Unknown'),
    ...afterSchoolSignIns.filter(s => s.dropOffTime && !s.pickUpTime).map(s => childrenById[s.childId]?.name || 'Unknown'),
  ];
  
  const allSignedOutChildren = [
    ...signedOutFullTime.map(s => childrenById[s.childId]?.name || 'Unknown'),
    ...stayAllDaySignedOut.map(s => s.childName || 'Unknown'),
    ...partTimeSignIns.filter(s => s.dropOffTime && s.pickUpTime).map(s => childrenById[s.childId]?.name || 'Unknown'),
    ...schoolAgeSignIns.filter(s => s.pmParentPickupTime && !s.stayAllDay).map(s => childrenById[s.childId]?.name || 'Unknown'),
    ...afterSchoolSignIns.filter(s => s.dropOffTime && s.pickUpTime).map(s => childrenById[s.childId]?.name || 'Unknown'),
  ];

  // Navigation handlers
  const goToPreviousDay = () => {
    const currentDate = parse(date, 'yyyy-MM-dd', new Date());
    const prevDate = subDays(currentDate, 1);
    navigate(`/signin/${format(prevDate, 'yyyy-MM-dd')}`);
  };

  const goToNextDay = () => {
    const currentDate = parse(date, 'yyyy-MM-dd', new Date());
    const nextDate = addDays(currentDate, 1);
    navigate(`/signin/${format(nextDate, 'yyyy-MM-dd')}`);
  };

  // ============== RENDER ==============
  
  return (
    <AppShell pageTitle={`Sign-In Sheet - ${format(parse(date, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')}`} breadcrumb>
      <div data-testid="daily-signin-page">
        {/* Header Component with Banner */}
        <div className="mb-8">
          {/* Banner Image Header - Carousel */}
          <DailySignInHeroCarousel>
            <div className="py-8 px-4 w-full">
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPreviousDay}
                  className="text-blue-900 hover:bg-white/50 rounded-full print:hidden"
                  data-testid="prev-day-btn"
                >
                  <ArrowLeft className="w-6 h-6" />
                </Button>
                
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-blue-900" data-testid="page-title">
                    Daily Sign-In
                  </h1>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <CalendarIcon className="w-5 h-5 text-blue-800" />
                    <span className="text-xl text-blue-900 font-medium">
                      {format(parse(date, 'yyyy-MM-dd', new Date()), 'EEEE')}, {format(parse(date, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')}
                    </span>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNextDay}
                  className="text-blue-900 hover:bg-white/50 rounded-full print:hidden"
                  data-testid="next-day-btn"
                >
                  <ArrowRight className="w-6 h-6" />
                </Button>
              </div>
              
              {/* Generate Month Button */}
              {signIns.length === 0 && (
                <div className="flex justify-center mt-4 print:hidden">
                  <Button
                    onClick={handleGenerateMonth}
                    disabled={generatingMonth}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                    data-testid="generate-month-btn"
                  >
                    {generatingMonth ? "Generating..." : "Generate Full Month"}
                  </Button>
                </div>
              )}
            </div>
          </DailySignInHeroCarousel>
          
          {/* Navigation Header */}
          <div className="flex items-center justify-between print:hidden">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-blue-900 hover:text-blue-950 hover:bg-blue-100 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">(save)</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                className="border-blue-300 text-blue-900 hover:bg-blue-100"
                data-testid="refresh-btn"
                title="Manual refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="border-blue-300 text-blue-900 hover:bg-blue-100"
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>

        {/* Attendance Summary Component */}
        <AttendanceSummary
          fullTimeSignIns={fullTimeSignIns}
          fullTimeCounts={fullTimeCounts}
          fullTimeSubcategoryCounts={fullTimeSubcategoryCounts}
          fullTimeSubcategoryChildren={fullTimeSubcategoryChildren}
          partTimeSignIns={partTimeSignIns}
          partTimeCounts={partTimeCounts}
          partTimeChildrenList={partTimeChildrenList}
          schoolAgeSignIns={schoolAgeSignIns}
          schoolAgeCounts={schoolAgeCounts}
          schoolAgeChildrenLists={schoolAgeChildrenLists}
          afterSchoolSignIns={afterSchoolSignIns}
          afterSchoolCounts={afterSchoolCounts}
          afterSchoolChildrenList={afterSchoolChildrenList}
          uncategorizedSignIns={uncategorizedSignIns}
          uncategorizedCounts={uncategorizedCounts}
          totalChildren={totalChildren}
          totalSignedIn={totalSignedIn}
          totalSignedOut={totalSignedOut}
          allSignedInChildren={allSignedInChildren}
          allSignedOutChildren={allSignedOutChildren}
          signedOutSubcategoryCounts={signedOutSubcategoryCounts}
          signedOutSubcategoryChildren={signedOutSubcategoryChildren}
          stayAllDayCount={stayAllDayCurrentlyIn.length}
          stayAllDayNames={stayAllDayCurrentlyIn.map(s => s.childName || 'Unknown')}
          midDayCurrentlyOut={midDayCurrentlyOut}
          midDayReturnedToday={midDayReturnedToday}
          stillInData={stillInData}
          date={date}
          currentUserName={user?.name || user?.email}
        />

        {/* Center Team Message - Broadcast to all rooms (Premium+) */}
        {hasCenterAlertsAccess && (
          <CenterTeamMessage 
            date={date} 
            currentUserEmail={user?.email}
            currentUserName={user?.name}
          />
        )}

        {/* Search and Filter Controls */}
        {signIns.length > 0 && (
          <div 
            className="mb-6 p-5 rounded-2xl print:hidden relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(191,219,254,0.85) 50%, rgba(224,231,255,0.9) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(59,130,246,0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
              border: '1px solid rgba(59,130,246,0.25)'
            }}
            data-testid="search-filter-controls"
          >
            {/* Decorative highlight */}
            <div 
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{
                background: 'linear-gradient(90deg, rgba(59,130,246,0.5) 0%, rgba(99,102,241,0.5) 50%, rgba(59,130,246,0.5) 100%)'
              }}
            />
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500" />
                <Input
                  type="text"
                  placeholder="Search by child name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 pr-10 border-blue-300 bg-white/90 focus:bg-white rounded-xl h-11 text-blue-900 placeholder:text-blue-400 shadow-sm"
                  data-testid="search-child-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-400 hover:text-blue-600 transition-colors"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="border border-blue-300 rounded-xl px-4 py-2.5 bg-white/90 text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[160px] shadow-sm font-medium cursor-pointer"
                  data-testid="category-filter-select"
                >
                  <option value="all">All Categories</option>
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="school-age">School Age</option>
                  <option value="after-school">After School</option>
                </select>
              </div>
              
              {/* Reset Button - only show when filters are active */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={resetFilters}
                  className="border-blue-400 bg-white/90 text-blue-700 hover:bg-white hover:text-blue-800 rounded-xl whitespace-nowrap shadow-sm h-11 px-4 font-medium"
                  data-testid="reset-filters-btn"
                >
                  <RotateCcw className="w-5 h-5 mr-1.5" />
                  Reset
                </Button>
              )}
            </div>
            
            {/* Filter Results Info */}
            {hasActiveFilters && (
              <div className="mt-4 text-sm text-blue-700 font-medium bg-white/50 rounded-lg px-3 py-2 inline-block">
                {categoryFilter === 'all' ? (
                  <>
                    Showing {searchFilteredSignIns.length} of {signIns.length} children
                    {searchQuery && <span className="ml-1">matching "{searchQuery}"</span>}
                  </>
                ) : (
                  <>
                    Showing {
                      categoryFilter === 'full-time' ? fullTimeSignIns.length :
                      categoryFilter === 'part-time' ? partTimeSignIns.length :
                      categoryFilter === 'school-age' ? schoolAgeSignIns.length :
                      categoryFilter === 'after-school' ? afterSchoolSignIns.length : 0
                    } {categoryFilter.replace('-', ' ')} children
                    {searchQuery && <span className="ml-1">matching "{searchQuery}"</span>}
                  </>
                )}
              </div>
            )}
            
            {/* Print Feature Instruction */}
            <div className="mt-3 text-xs text-blue-600 italic flex items-center gap-1">
              <Printer className="w-3 h-3" />
              <span>To print individual category, select from above</span>
            </div>
          </div>
        )}

        {/* Main Content */}
        {loading ? (
          <p className="text-center text-brand-primary mt-8">Loading...</p>
        ) : signIns.length === 0 ? (
          <Card className="mt-8 border-0 shadow-xl" style={{
            background: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
          }}>
            <CardContent className="p-12 text-center">
              <p className="text-xl text-ink-main mb-4">No children to sign in for this date.</p>
              <div className="space-x-4">
                <Button
                  onClick={() => navigate('/children')}
                  className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
                >
                  Add Children
                </Button>
                <Button
                  onClick={handleGenerateMonth}
                  className="bg-brand-secondary hover:bg-brand-secondary/90 text-white rounded-xl"
                >
                  Generate Month Records
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8 mt-8">
            {/* No Results Message */}
            {hasActiveFilters && searchFilteredSignIns.length === 0 && (
              <Card className="border-0" style={{
                background: 'rgba(255,255,255,0.75)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
              }}>
                <CardContent className="p-8 text-center">
                  <p className="text-lg text-gray-600">No children found matching your search.</p>
                  <Button
                    variant="outline"
                    onClick={resetFilters}
                    className="mt-4 border-blue-300 text-blue-700 hover:bg-blue-50 rounded-xl"
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Full Time Children Section */}
            {showFullTime && fullTimeSignIns.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4" data-testid="regular-section-title">
                  Full Time ({fullTimeSignIns.length})
                </h2>
                <div className="space-y-4">
                  {fullTimeSignIns.map((signIn, index) => (
                    <RegularSignInCard
                      key={signIn.id}
                      signIn={signIn}
                      child={getChild(signIn.childId)}
                      index={index}
                      colorScheme="blue"
                      onDropOff={handleDropOff}
                      onClearDropOff={handleClearDropOff}
                      onPickUp={handlePickUp}
                      onClearPickUp={handleClearPickUp}
                      onAttendanceToggle={handleAttendanceToggle}
                      onNotesChange={handleNotesUpdate}
                      onReactivate={handleReactivate}
                      onManualTimeUpdate={handleManualTimeUpdate}
                      onSaveTimeToServer={saveTimeToServer}
                      onManualPersonEntry={handleManualPersonEntry}
                      onMidDayOut={handleMidDayOut}
                      onMidDayReturn={handleMidDayReturn}
                      onClearMidDay={handleClearMidDay}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Part Time Children Section */}
            {showPartTime && partTimeSignIns.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-amber-800 mb-4" data-testid="part-time-section-title">
                  Part Time ({partTimeSignIns.length})
                </h2>
                <div className="space-y-4">
                  {partTimeSignIns.map((signIn, index) => (
                    <RegularSignInCard
                      key={signIn.id}
                      signIn={signIn}
                      child={getChild(signIn.childId)}
                      index={index}
                      colorScheme="purple"
                      onDropOff={handleDropOff}
                      onClearDropOff={handleClearDropOff}
                      onPickUp={handlePickUp}
                      onClearPickUp={handleClearPickUp}
                      onAttendanceToggle={handleAttendanceToggle}
                      onNotesChange={handleNotesUpdate}
                      onReactivate={handleReactivate}
                      onManualTimeUpdate={handleManualTimeUpdate}
                      onSaveTimeToServer={saveTimeToServer}
                      onManualPersonEntry={handleManualPersonEntry}
                      onMidDayOut={handleMidDayOut}
                      onMidDayReturn={handleMidDayReturn}
                      onClearMidDay={handleClearMidDay}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* School Age Children Section */}
            {showSchoolAge && schoolAgeSignIns.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-blue-800 mb-2" data-testid="school-age-section-title">
                  School Age ({schoolAgeSignIns.length})
                </h2>
                <div className="mb-4 text-sm">
                  <p className="text-purple-700 font-medium">
                    AM Signed In Today: {schoolAgeCounts.amSignedIn}
                  </p>
                  <p className="text-green-700 font-medium">
                    PM Drop Off Today: {schoolAgeCounts.pmDropOff}
                  </p>
                  <p className="text-blue-700 font-medium">
                    PM Out: {schoolAgeCounts.pmPickedUp}
                  </p>
                </div>
                <div className="space-y-4">
                  {schoolAgeSignIns.map((signIn, index) => (
                    <SchoolAgeSignInCard
                      key={signIn.id}
                      signIn={signIn}
                      child={getChild(signIn.childId)}
                      pickupPersons={getPickupPersonsForChild(signIn.childId)}
                      index={index}
                      onAmDropOff={handleAmDropOff}
                      onClearAmDropOff={handleClearAmDropOff}
                      onPmBusDropoff={handlePmBusDropoff}
                      onClearPmBus={handleClearPmBusDropoff}
                      onPmParentPickup={handlePmParentPickup}
                      onClearPmParent={handleClearPmParentPickup}
                      onManualTimeUpdate={handleManualTimeUpdate}
                      onSaveTimeToServer={saveTimeToServer}
                      onNotesChange={handleNotesUpdate}
                      onReactivate={handleReactivate}
                      onManualPersonEntry={handleManualPersonEntry}
                      onStayAllDayToggle={handleStayAllDayToggle}
                      onMidDayOut={handleMidDayOut}
                      onMidDayReturn={handleMidDayReturn}
                      onClearMidDay={handleClearMidDay}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* After School Children Section */}
            {showAfterSchool && afterSchoolSignIns.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-amber-800 mb-4" data-testid="after-school-section-title">
                  After School ({afterSchoolSignIns.length})
                </h2>
                <div className="space-y-4">
                  {afterSchoolSignIns.map((signIn, index) => (
                    <RegularSignInCard
                      key={signIn.id}
                      signIn={signIn}
                      child={getChild(signIn.childId)}
                      index={index}
                      colorScheme="purple"
                      onDropOff={handleDropOff}
                      onClearDropOff={handleClearDropOff}
                      onPickUp={handlePickUp}
                      onClearPickUp={handleClearPickUp}
                      onAttendanceToggle={handleAttendanceToggle}
                      onNotesChange={handleNotesUpdate}
                      onReactivate={handleReactivate}
                      onManualTimeUpdate={handleManualTimeUpdate}
                      onSaveTimeToServer={saveTimeToServer}
                      onManualPersonEntry={handleManualPersonEntry}
                      onMidDayOut={handleMidDayOut}
                      onMidDayReturn={handleMidDayReturn}
                      onClearMidDay={handleClearMidDay}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Uncategorized Children Section */}
            {showUncategorized && uncategorizedSignIns.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-700 mb-4" data-testid="uncategorized-section-title">
                  Other ({uncategorizedSignIns.length})
                </h2>
                <div className="space-y-4">
                  {uncategorizedSignIns.map((signIn, index) => (
                    <RegularSignInCard
                      key={signIn.id}
                      signIn={signIn}
                      child={getChild(signIn.childId)}
                      index={index}
                      onDropOff={handleDropOff}
                      onClearDropOff={handleClearDropOff}
                      onPickUp={handlePickUp}
                      onClearPickUp={handleClearPickUp}
                      onAttendanceToggle={handleAttendanceToggle}
                      onNotesChange={handleNotesUpdate}
                      onReactivate={handleReactivate}
                      onManualTimeUpdate={handleManualTimeUpdate}
                      onSaveTimeToServer={saveTimeToServer}
                      onManualPersonEntry={handleManualPersonEntry}
                      onMidDayOut={handleMidDayOut}
                      onMidDayReturn={handleMidDayReturn}
                      onClearMidDay={handleClearMidDay}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default DailySignInPage;
